import classNames from 'classnames'
import moment from 'moment'
import * as React from 'react'
import { Link } from 'react-router-dom'
import { Column, usePagination, useSortBy, useTable } from 'react-table'
import ReactTooltip from 'react-tooltip'
import ReactTablePagination from 'src/components/ui/ReactTablePagination'
import { IMembershipRiderObject, IRiderFilters, IRiderSearchObject } from 'src/types'

interface IServiceViewMemberProps {
  serviceId: string
  members: any
  totalItems: number
  getMembers: (filters: IRiderFilters) => Promise<any>
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  filters: IRiderFilters
  pageCount: number
  fetchData: (params: { filters: IRiderFilters; pageIndex: number; pageSize: number }) => void
}

const PAGE_SIZE = 10

function Table<T extends object>({
  columns,
  data,
  fetchData,
  loading,
  filters,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize, sortBy } = state

  React.useEffect(() => {
    fetchData({ filters, pageIndex, pageSize })
  }, [filters, pageIndex, pageSize])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })
    fetchData({ filters: { ...filters, sortBy: sortParams }, pageIndex, pageSize })
  }, [sortBy])

  return (
    <React.Fragment>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td>Loading...</td>
            </tr>
          ) : (
            rows.map(
              (row, i) =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, index) => {
                      return (
                        <td {...cell.getCellProps()} key={`1-${index}`}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

const buildMembershipCard = (membership: IMembershipRiderObject) => {
  const serviceName = membership && membership.service && membership.service.name ? membership.service.name : ''
  const externalId = membership && membership.externalRiderId ? `, ${membership.externalRiderId}` : ''
  return (
    <div className='m1 rounded border-grey4 bg-white font-grey3'>
      <div className='h6 bold ml2 mr2 mt02 mb02' style={{ wordBreak: 'break-all' }}>
        {serviceName}
        {externalId}
      </div>
    </div>
  )
}

const renderEmail = (rider: IRiderSearchObject) => {
  if (!rider || !rider.email) {
    return ''
  }

  return rider.email
}

const renderUsername = (rider: IRiderSearchObject) => {
  if (!rider || !rider.username) {
    return ''
  }

  return rider.username
}

const renderCreatedDate = (rider: IRiderSearchObject) => {
  if (!rider || !rider.createdAt) {
    return ''
  }
  return moment(rider.createdAt).format('MMM DD YYYY')
}

export const ServiceViewMember = (props: IServiceViewMemberProps) => {
  const [loading, setLoading] = React.useState(false)
  const [filters, setFilters] = React.useState({
    isFirstArchived: true,
    isSecondArchived: false,
    isThirdArchived: false,
    membershipServiceIds: [props.serviceId]
  })

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        width: 200,
        accessor: (rider: IRiderSearchObject) => {
          return <Link to={`/riders/${rider.id}/profile/info`}>{rider.name}</Link>
        }
      },
      {
        id: 'phone',
        Header: 'Phone Number',
        accessor: 'phone',
        disableSortBy: true,
        className: 'flex items-center'
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: (rider: IRiderSearchObject) => renderEmail(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'username',
        Header: 'Username',
        accessor: (rider: IRiderSearchObject) => renderUsername(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'memberships',
        Header: 'Memberships',
        accessor: 'membership',
        className: 'flex items-center',
        disableSortBy: true,
        Cell: (data: any) => {
          const rider = data.row.original
          if (!rider.serviceMemberships || !rider.serviceMemberships.length) {
            return ''
          }
          let temp = ''

          rider.serviceMemberships.map((v: IMembershipRiderObject, key: number) => {
            temp = temp + v.service.name + ', '
          })

          return temp.slice(0, -2)
          // code to display memnership as tiles
          // return (
          //   <div className='flex'>
          //     {rider.serviceMemberships.length > 0 && <div>{buildMembershipCard(rider.serviceMemberships[0])}</div>}
          //     {rider.serviceMemberships.length > 1 && (
          //       <div>
          //         <div
          //           data-tip={'change status' + data.row.id}
          //           data-for={'change status' + data.row.id}
          //           className='m1 rounded border-grey4 bg-white font-grey3'
          //         >
          //           <div className='h6 bold ml2 mr2 mt02 mb02'>
          //             +{rider.serviceMemberships.length - 1} More Membership
          //           </div>
          //         </div>
          //         <ReactTooltip
          //           id={'change status' + data.row.id}
          //           aria-haspopup='true'
          //           effect='solid'
          //           place='right'
          //           className='shadow'
          //           delayHide={200}
          //           delayUpdate={200}
          //           backgroundColor='#fff'
          //         >
          //           <div className='ListItemMenu flex-auto' style={{ width: '300px' }}>
          //             <div className='col-12 center pt1 bt1 bl1 br1 rounded bold cursor--pointer'>
          //               {rider.serviceMemberships.map((v: IMembershipRiderObject, key: number) => (
          //                 <div key={key}>{buildMembershipCard(v)}</div>
          //               ))}
          //             </div>
          //           </div>
          //         </ReactTooltip>
          //       </div>
          //     )}
          //   </div>
          // )
        }
      },
      {
        id: 'createdAt',
        Header: 'Created Date',
        accessor: (rider: IRiderSearchObject) => renderCreatedDate(rider),
        className: 'flex items-center'
      }
    ],
    []
  )

  const fetchData = React.useCallback(async ({ filters, pageSize, pageIndex }) => {
    setLoading(true)
    await props.getMembers({ ...filters, skip: pageIndex * pageSize, take: pageSize })
    setLoading(false)
  }, [filters])

  const pageCount = Math.ceil(props.totalItems / PAGE_SIZE)
  return (
    <React.Fragment>
      <div className='react-table'>
        <Table
          columns={columns}
          data={props.members}
          fetchData={fetchData}
          filters={filters}
          loading={loading}
          pageCount={pageCount}
        />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/services/view/ServiceViewMember.tsx